import { supabase } from "../../../shared/supabase/client";
import {
  createDeferredEncryptedMediaKeyEnvelope,
  prepareEncryptedMediaTransfer,
  receiveEncryptedBinaryTransfer,
  receiveEncryptedMediaTransfer,
  uploadPreparedEncryptedMediaTransfer,
  type RelayRecipientDeviceKey
} from "../../../shared/platform/encryptedMediaRelay";
import { cacheReceivedV2DepthField, decodeV2DepthField } from "../../../shared/particles/particlePhotoLocalDepth";
import type { RuntimeProbe } from "../../../shared/platform/runtime";
import { encryptedRelayErrorMessage } from "../model/relayErrorMessage";
import { syncFailureKindFromError, type SyncFailureKind } from "../model/syncFailure";
import { scheduleV2DepthGeneration } from "./v2DepthGenerationService";

interface TargetDeviceKeyRow {
  device_id: string;
  device_key_id: string;
  key_version: number;
  algorithm: string;
  public_key: string;
}

interface StagedEncryptedTransfer {
  id: string;
  storagePath: string;
  sourceDeviceId: string;
  sourceSha256: string;
  ciphertextSha256: string;
}

interface DeferredRecipientDeviceKeyRow {
  transfer_id: string;
  content_id: string;
  source_envelope: string;
  recipient_device_id: string;
  recipient_key_id: string;
  recipient_public_key: string;
}

export interface EncryptedMediaSyncResult {
  changed: boolean;
  pendingUploads: number;
  pendingDownloads: number;
  failedAttempts: number;
  failureKinds: SyncFailureKind[];
}

function client() {
  if (!supabase) throw new Error("Supabase 尚未配置");
  return supabase;
}

function unavailable(error: { code?: string } | null) {
  return error?.code === "PGRST205" || error?.code === "42P01" || error?.code === "PGRST202";
}

function attemptErrorCode(error: unknown) {
  const message = error instanceof Error ? error.message : "";
  if (message.includes("校验")) return "integrity";
  if (message.includes("解密") || message.includes("密钥")) return "decrypt";
  if (message.includes("本机") || message.includes("持久")) return "storage";
  return "network";
}

function relayError(error: unknown) {
  const message = encryptedRelayErrorMessage(error);
  if (message) return new Error(message);
  return error;
}

async function reportUploadAttempt(transferId: string, errorCode: string | null) {
  const { error } = await client().rpc("report_encrypted_media_upload_attempt", {
    target_transfer_id: transferId,
    target_error_code: errorCode
  });
  if (error && !unavailable(error)) throw error;
}

async function reportDownloadAttempt(transferId: string, recipientDeviceId: string, errorCode: string | null) {
  const { error } = await client().rpc("report_encrypted_media_download_attempt", {
    target_transfer_id: transferId,
    target_recipient_device_id: recipientDeviceId,
    target_error_code: errorCode
  });
  if (error && !unavailable(error)) throw error;
}

async function requestRelayMaintenance() {
  const { error } = await client().functions.invoke("media-relay-maintenance", { method: "POST" });
  if (error && !unavailable(error as { code?: string })) {
    // Cleanup has its own retry path. A temporary maintenance failure must not invalidate a receipt.
  }
}

async function appendDeferredRecipientEnvelopes(runtime: RuntimeProbe) {
  const empty = { changed: false, failedAttempts: 0, failureKinds: [] as SyncFailureKind[] };
  if (!runtime.deviceId) return empty;
  const database = client();
  const { data: rows, error } = await database.rpc("list_pending_encrypted_media_recipient_keys", {
    target_source_device_id: runtime.deviceId
  });
  if (error) {
    if (unavailable(error)) return empty;
    throw error;
  }

  let changed = false;
  let failedAttempts = 0;
  const failureKinds: SyncFailureKind[] = [];
  for (const row of (rows ?? []) as DeferredRecipientDeviceKeyRow[]) {
    try {
      const envelope = await createDeferredEncryptedMediaKeyEnvelope({
        mediaId: row.content_id,
        sourceEnvelope: row.source_envelope,
        recipient: {
          recipientDeviceId: row.recipient_device_id,
          recipientKeyId: row.recipient_key_id,
          publicKey: row.recipient_public_key
        }
      });
      const { error: appendError } = await database.rpc("append_encrypted_media_transfer_recipient", {
        target_transfer_id: row.transfer_id,
        target_recipient_device_id: envelope.recipientDeviceId,
        target_recipient_key_id: envelope.recipientKeyId,
        target_envelope: envelope.envelope
      });
      if (appendError) throw appendError;
      changed = true;
    } catch (error) {
      // The source device can safely retry later because it retains its own key envelope.
      failedAttempts += 1;
      failureKinds.push(syncFailureKindFromError(error));
    }
  }
  return { changed, failedAttempts, failureKinds };
}

function asRecipients(rows: TargetDeviceKeyRow[]) {
  const recipients: RelayRecipientDeviceKey[] = rows.map((row) => ({
    recipientDeviceId: row.device_id,
    recipientKeyId: row.device_key_id,
    publicKey: row.public_key
  }));
  if (recipients.length === 0 || recipients.some((recipient) => !recipient.publicKey)) {
    throw new Error("这条星河还有成员未完成安全同步准备");
  }
  return recipients;
}

export async function stageEncryptedMediaTransfer(galaxyId: string, mediaId: string, runtime: RuntimeProbe): Promise<StagedEncryptedTransfer> {
  if (!runtime.storageReady || !runtime.deviceId) {
    throw new Error("这台设备的安全本地存储尚未准备好");
  }
  const database = client();
  const { data: keys, error: keysError } = await database.rpc("list_media_target_device_keys", { target_media_id: mediaId });
  if (keysError) throw relayError(keysError);
  const transferId = crypto.randomUUID();
  const prepared = await prepareEncryptedMediaTransfer(transferId, mediaId, asRecipients((keys ?? []) as TargetDeviceKeyRow[]));
  if (prepared.sourceDeviceId !== runtime.deviceId || prepared.algorithm !== "x25519-xchacha20poly1305-v1") {
    throw new Error("本机加密中转准备结果无效");
  }
  const storagePath = `${galaxyId}/relay/${transferId}.bin`;
  const { error: createError } = await database.rpc("create_encrypted_media_transfer", {
    target_transfer_id: transferId,
    target_media_id: mediaId,
    target_source_device_id: runtime.deviceId,
    target_storage_path: storagePath,
    target_source_sha256: prepared.sourceSha256,
    target_ciphertext_sha256: prepared.ciphertextSha256,
    target_ciphertext_size: prepared.ciphertextSize,
    target_envelopes: prepared.envelopes.map((envelope) => ({
      recipient_device_id: envelope.recipientDeviceId,
      recipient_key_id: envelope.recipientKeyId,
      envelope: envelope.envelope
    }))
  });
  if (createError) throw relayError(createError);
  return {
    id: transferId,
    storagePath,
    sourceDeviceId: prepared.sourceDeviceId,
    sourceSha256: prepared.sourceSha256,
    ciphertextSha256: prepared.ciphertextSha256
  };
}

export async function uploadStagedEncryptedMediaTransfer(transfer: StagedEncryptedTransfer) {
  try {
    const database = client();
    const { data: signedUpload, error: signError } = await database.storage
      .from("memory-relay")
      .createSignedUploadUrl(transfer.storagePath);
    if (signError) throw signError;
    const uploaded = await uploadPreparedEncryptedMediaTransfer(transfer.id, signedUpload.signedUrl);
    if (uploaded.ciphertextSha256 !== transfer.ciphertextSha256) {
      throw new Error("加密中转上传校验不一致");
    }
    const { error: markError } = await database.rpc("mark_encrypted_media_transfer_uploaded", {
      target_transfer_id: transfer.id,
      observed_ciphertext_sha256: uploaded.ciphertextSha256
    });
    if (markError) throw markError;
    await reportUploadAttempt(transfer.id, null);
    const { error: receiptError } = await database.rpc("record_encrypted_media_delivery_receipt", {
      target_transfer_id: transfer.id,
      target_recipient_device_id: transfer.sourceDeviceId,
      verified_source_sha256: transfer.sourceSha256
    });
    if (receiptError) throw receiptError;
    void requestRelayMaintenance();
  } catch (error) {
    await reportUploadAttempt(transfer.id, attemptErrorCode(error));
    throw error;
  }
}

async function resumeStagedEncryptedMediaTransfer(job: {
  id: string;
  storage_path: string;
  source_device_id: string;
  source_sha256: string;
  ciphertext_sha256: string;
}) {
  const database = client();
  const { data: signedUpload, error: signError } = await database.storage
    .from("memory-relay")
    .createSignedUploadUrl(job.storage_path);
  if (signError) throw signError;
  const uploaded = await uploadPreparedEncryptedMediaTransfer(job.id, signedUpload.signedUrl);
  if (uploaded.ciphertextSha256 !== job.ciphertext_sha256) {
    throw new Error("加密中转上传校验不一致");
  }
  const { error: markError } = await database.rpc("mark_encrypted_media_transfer_uploaded", {
    target_transfer_id: job.id,
    observed_ciphertext_sha256: uploaded.ciphertextSha256
  });
  if (markError) throw markError;
  await reportUploadAttempt(job.id, null);
  const { error: receiptError } = await database.rpc("record_encrypted_media_delivery_receipt", {
    target_transfer_id: job.id,
    target_recipient_device_id: job.source_device_id,
    verified_source_sha256: job.source_sha256
  });
  if (receiptError) throw receiptError;
}

export async function synchronisePendingEncryptedMedia(runtime: RuntimeProbe): Promise<EncryptedMediaSyncResult> {
  const empty = { changed: false, pendingUploads: 0, pendingDownloads: 0, failedAttempts: 0, failureKinds: [] as SyncFailureKind[] };
  if (!runtime.storageReady || !runtime.deviceId || !supabase) return empty;
  const database = client();
  const deferredRecipients = await appendDeferredRecipientEnvelopes(runtime);
  const { data: stagedJobs, error: stagedJobsError } = await database
    .from("media_transfer_jobs")
    .select("id, storage_path, source_device_id, source_sha256, ciphertext_sha256")
    .eq("source_device_id", runtime.deviceId)
    .eq("state", "staged");
  if (stagedJobsError) {
    if (unavailable(stagedJobsError)) return {
      changed: deferredRecipients.changed,
      pendingUploads: 0,
      pendingDownloads: 0,
      failedAttempts: deferredRecipients.failedAttempts,
      failureKinds: deferredRecipients.failureKinds
    };
    throw stagedJobsError;
  }
  let changed = deferredRecipients.changed;
  let failedAttempts = deferredRecipients.failedAttempts;
  const failureKinds = [...deferredRecipients.failureKinds];
  let completedUploads = 0;
  for (const job of stagedJobs ?? []) {
    try {
      await resumeStagedEncryptedMediaTransfer(job);
      changed = true;
      completedUploads += 1;
    } catch (error) {
      failedAttempts += 1;
      failureKinds.push(syncFailureKindFromError(error));
      try {
        await reportUploadAttempt(job.id, attemptErrorCode(error));
      } catch {
        // The encrypted outbox remains in the app-private ledger for a later retry.
      }
    }
  }
  const { data: recipientRows, error: recipientsError } = await database
    .from("media_transfer_recipient_devices")
    .select("transfer_id, recipient_device_id")
    .eq("recipient_device_id", runtime.deviceId);
  if (recipientsError) {
    if (unavailable(recipientsError)) return {
      changed,
      pendingUploads: Math.max(0, (stagedJobs?.length ?? 0) - completedUploads),
      pendingDownloads: 0,
      failedAttempts,
      failureKinds: [...new Set(failureKinds)]
    };
    throw recipientsError;
  }
  const { data: receiptRows, error: receiptsError } = await database
    .from("media_delivery_receipts")
    .select("transfer_id")
    .eq("recipient_device_id", runtime.deviceId);
  if (receiptsError) {
    if (unavailable(receiptsError)) return {
      changed,
      pendingUploads: Math.max(0, (stagedJobs?.length ?? 0) - completedUploads),
      pendingDownloads: 0,
      failedAttempts,
      failureKinds: [...new Set(failureKinds)]
    };
    throw receiptsError;
  }
  const receivedTransferIds = new Set((receiptRows ?? []).map((receipt) => receipt.transfer_id));
  let completedDownloads = 0;
  for (const recipient of recipientRows ?? []) {
    if (receivedTransferIds.has(recipient.transfer_id)) continue;
    try {
      const { data: job, error: jobError } = await database
        .from("media_transfer_jobs")
        .select("id, media_id, content_id, transfer_kind, storage_path, source_sha256, ciphertext_sha256, ciphertext_size, state")
        .eq("id", recipient.transfer_id)
        .in("state", ["uploaded", "cleanup_ready"])
        .maybeSingle();
      if (jobError) throw jobError;
      if (!job) continue;
      const { data: envelope, error: envelopeError } = await database
        .from("media_transfer_key_envelopes")
        .select("envelope")
        .eq("transfer_id", job.id)
        .eq("recipient_device_id", runtime.deviceId)
        .maybeSingle();
      if (envelopeError) throw envelopeError;
      if (!envelope) continue;
      const { data: signedDownload, error: signError } = await database.storage
        .from("memory-relay")
        .createSignedUrl(job.storage_path, 300);
      if (signError) throw signError;
      let receivedSha256: string;
      if (job.transfer_kind === "depth_v2") {
        const copy = await receiveEncryptedBinaryTransfer({
          transferId: job.id,
          contentId: job.content_id,
          recipientDeviceId: runtime.deviceId,
          envelope: envelope.envelope,
          downloadUrl: signedDownload.signedUrl,
          expectedSourceSha256: job.source_sha256,
          expectedCiphertextSha256: job.ciphertext_sha256,
          expectedCiphertextSize: job.ciphertext_size
        });
        const depth = decodeV2DepthField(copy.bytes);
        if (!depth) throw new Error("V2 深度资源格式无效");
        await cacheReceivedV2DepthField(job.media_id, depth);
        window.dispatchEvent(new CustomEvent("memory-universe:v2-depth-ready", { detail: job.media_id }));
        receivedSha256 = copy.sha256;
      } else {
        const { data: media, error: mediaError } = await database
          .from("media_assets")
          .select("file_name, mime_type, space_id")
          .eq("id", job.media_id)
          .maybeSingle();
        if (mediaError) throw mediaError;
        if (!media) continue;
        const copy = await receiveEncryptedMediaTransfer({
          transferId: job.id,
          mediaId: job.content_id,
          recipientDeviceId: runtime.deviceId,
          envelope: envelope.envelope,
          downloadUrl: signedDownload.signedUrl,
          expectedSourceSha256: job.source_sha256,
          expectedCiphertextSha256: job.ciphertext_sha256,
          expectedCiphertextSize: job.ciphertext_size,
          fileName: media.file_name,
          mimeType: media.mime_type
        });
        receivedSha256 = copy.sha256;
        // The original has reached durable local storage. Queue V2 directly
        // from the receipt path instead of waiting for a later memory refresh.
        scheduleV2DepthGeneration({ galaxyId: String(media.space_id), mediaId: job.media_id, runtime });
      }
      const { error: receiptError } = await database.rpc("record_encrypted_media_delivery_receipt", {
        target_transfer_id: job.id,
        target_recipient_device_id: runtime.deviceId,
        verified_source_sha256: receivedSha256
      });
      if (receiptError) throw receiptError;
      void requestRelayMaintenance();
      await reportDownloadAttempt(job.id, runtime.deviceId, null);
      completedDownloads += 1;
      changed = true;
    } catch (error) {
      failedAttempts += 1;
      failureKinds.push(syncFailureKindFromError(error));
      try {
        await reportDownloadAttempt(recipient.transfer_id, runtime.deviceId, attemptErrorCode(error));
      } catch {
        // The remote job and local durable copy remain available for the next retry.
      }
    }
  }
  if (changed) void requestRelayMaintenance();
  return {
    changed,
    pendingUploads: Math.max(0, (stagedJobs?.length ?? 0) - completedUploads),
    pendingDownloads: Math.max(
      0,
      (recipientRows ?? []).filter((recipient) => !receivedTransferIds.has(recipient.transfer_id)).length - completedDownloads
    ),
    failedAttempts,
    failureKinds: [...new Set(failureKinds)]
  };
}
